import type { List } from 'immutable';

import type { HoldingNodeType } from 'app-components/flux/investment-holdings-v2';
import { getLogger } from 'app-components/utils/core';

const log = getLogger('flux/ai-insights/transformers/investmentsTransformers');

// TODO (rakula): Is this enough for LLMs to understand?
export interface AIPortfolioData {
  portfolioSummary: {
    totalPositions: number;
    totalValue: number;
    totalCostBasis: number;
    totalGainLoss: number;
    totalGainLossPercent: number;
    dataAsOf: string;
  };
  positions: PortfolioPosition[];
}

interface PortfolioPosition {
  name: string;
  symbol: string;
  type: string;
  shares: number;
  currentPrice: number;
  marketValue: number;
  portfolioWeight: number;
  costBasis?: number;
  gainLoss?: number;
  gainLossPercent?: number;
  dayChange?: number;
  dayChangePercent?: number;
  marketData?: {
    volume?: number;
    marketCap?: number;
    peRatio?: number;
    dividendYield?: number;
    yearHigh?: number;
    yearLow?: number;
    priceFromHigh?: number;
    priceFromLow?: number;
  };
  dataQuality: {
    hasCostBasis: boolean;
    hasMarketData: boolean;
  };
  accounts: {
    accountId: string;
    shares: number;
    value: number;
    costBasis?: number;
  }[];
}

const round = (num: number): number => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

// TODO (rakula): Is this the best way to determine security type?
const getSecurityType = (name: string): string => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('bond') || lowerName.includes('treasury')) return 'bond';
  if (lowerName.includes('fund') || lowerName.includes('etf')) return 'fund';
  return 'stock';
};

/**
 * Clean individual position data
 */
const cleanPosition = (holding: HoldingNodeType, totalValue: number): PortfolioPosition => {
  const gainLoss = holding.netGain ?? (holding.sumOfCostBasis ? holding.value - holding.sumOfCostBasis : undefined);
  const gainLossPercent = gainLoss && holding.sumOfCostBasis ? (gainLoss / holding.sumOfCostBasis) * 100 : undefined;

  const cleanPos: PortfolioPosition = {
    name: (holding.name ?? '').trim(),
    symbol: holding.symbol || holding.key,
    type: getSecurityType(holding.name ?? ''),
    shares: Math.abs(holding.shares),
    currentPrice: holding.price,
    marketValue: Math.abs(holding.value),
    portfolioWeight: round((Math.abs(holding.value) / totalValue) * 100),
    dataQuality: {
      hasCostBasis: !holding.costBasisStatus?.noCostBasis,
      hasMarketData: !!holding.quote,
    },
    accounts: holding.holdings.map((h) => ({
      accountId: h.accountId,
      shares: h.quantity ?? 0,
      value: h.marketValue ?? 0,
      ...(h.userCostBasis && { costBasis: h.userCostBasis }),
    })),
  };

  // Add optional fields only if they exist
  if (holding.sumOfCostBasis) cleanPos.costBasis = holding.sumOfCostBasis;
  if (gainLoss !== undefined) cleanPos.gainLoss = round(gainLoss);
  if (gainLossPercent !== undefined) cleanPos.gainLossPercent = round(gainLossPercent);
  if (holding.todayValue) cleanPos.dayChange = holding.todayValue;
  if (holding.todayPercent) cleanPos.dayChangePercent = holding.todayPercent;

  // Add market data if available
  if (holding.quote) {
    cleanPos.marketData = {
      ...(holding.quote.volume && { volume: holding.quote.volume }),
      ...(holding.quote.mktCap && { marketCap: holding.quote.mktCap }),
      ...(holding.quote.peRatio && { peRatio: holding.quote.peRatio }),
      ...(holding.quote.divYield && { dividendYield: holding.quote.divYield }),
      ...(holding.quote.yearHigh && { yearHigh: holding.quote.yearHigh }),
      ...(holding.quote.yearLow && { yearLow: holding.quote.yearLow }),
      ...(holding.quote.yearHigh && {
        priceFromHigh: round(((holding.price - holding.quote.yearHigh) / holding.quote.yearHigh) * 100),
      }),
      ...(holding.quote.yearLow && {
        priceFromLow: round(((holding.price - holding.quote.yearLow) / holding.quote.yearLow) * 100),
      }),
    };
  }

  return cleanPos;
};

export const extractPortfolioDataForAI = ({
  holdings,
  includeHoldings = false,
}: {
  holdings?: List<HoldingNodeType>;
  includeHoldings?: boolean;
}): AIPortfolioData | undefined => {
  if (!holdings || holdings.size === 0) {
    log.error('No portfolio holdings provided');
    return undefined;
  }

  // Calculate portfolio totals
  const totalValue = holdings.reduce((sum, h) => sum + Math.abs(h.value), 0);
  const totalCostBasis = holdings.reduce((sum, h) => sum + (h.sumOfCostBasis || 0), 0);
  const totalGainLoss = holdings.reduce((sum, h) => sum + (h.netGain || 0), 0);

  const portfolioSummary = {
    totalPositions: holdings.size,
    totalValue: includeHoldings ? round(totalValue) : 0,
    totalCostBasis: includeHoldings ? round(totalCostBasis) : 0,
    totalGainLoss: includeHoldings ? round(totalGainLoss) : 0,
    totalGainLossPercent: totalCostBasis > 0 ? round((totalGainLoss / totalCostBasis) * 100) : 0,
    dataAsOf: new Date().toISOString(),
  };

  const positions = includeHoldings ? holdings.map((holding) => cleanPosition(holding, totalValue)).toArray() : []; // Return empty array when hiding holdings

  return {
    portfolioSummary,
    positions,
  };
};
